import { useEffect } from 'react';
import { useLocation } from 'wouter';
import { useAuth } from '@clerk/react';
import { setReviewerTokenProvider, setReviewerClaimId } from '@edison/api-client-react';
import { usePortalStore } from '@/store/portal-store.js';

interface Props {
  children: React.ReactNode;
}

/**
 * Wraps the grant-mode portal routes (/reviewer/portal/*). Registers the current Clerk session's
 * getToken() closure + the selected claimId with the shared portal data layer
 * (@edison/api-client-react's makePortalInstance grant mode), so every /api/reviewer/* request
 * carries a fresh Bearer token + X-Claim-Id (never a cached/stale token).
 *
 * If there is no active grant session (mode !== 'grant', e.g. after a hard refresh — the
 * in-memory portal store resets, but the reviewer's Clerk session itself persists across
 * refresh), redirects to /reviewer so they can re-pick their claim.
 */
export function ReviewerPortalGate({ children }: Props) {
  const { getToken } = useAuth();
  const mode = usePortalStore((s) => s.mode);
  const claimId = usePortalStore((s) => s.claimId);
  const [, navigate] = useLocation();

  const hasActiveSession = mode === 'grant' && !!claimId;

  // Set during render (not in a useEffect) so child effects see the provider already set.
  // React fires child effects before parent effects, so a parent useEffect would be too late —
  // PortalShell's useEffect would fire first with _reviewerTokenProvider still null → 401.
  // Module-level variable assignment during render is safe (idempotent, not React state).
  if (hasActiveSession) {
    setReviewerTokenProvider(() => getToken());
    setReviewerClaimId(claimId);
  }

  // Cleanup on unmount only.
  useEffect(() => {
    return () => {
      setReviewerTokenProvider(null);
      setReviewerClaimId(null);
    };
  }, []);

  useEffect(() => {
    if (!hasActiveSession) {
      navigate('/reviewer');
    }
  }, [hasActiveSession, navigate]);

  if (!hasActiveSession) return null;

  return <>{children}</>;
}
